import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import logo from '../../logo.png';
import { Plus, Edit2, Trash2, X, Check, ArrowUp, ArrowDown } from 'lucide-react';

export default function CatalogoProgramas() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [programas, setProgramas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mensaje, setMensaje] = useState(null);
  const [editando, setEditando] = useState(null); // id en edición
  const [formEdit, setFormEdit] = useState({ etiqueta: '', orden: 0 });
  const [mostrarNuevo, setMostrarNuevo] = useState(false);
  const [formNuevo, setFormNuevo] = useState({ clave: '', etiqueta: '', orden: 0 });

  const puedeAcceder = ['superadmin', 'coordinador_general'].includes(user?.rol_nombre || user?.rol);

  useEffect(() => {
    if (puedeAcceder) fetchProgramas();
    else setLoading(false);
  }, []);

  const fetchProgramas = async () => {
    setLoading(true);
    try {
      const r = await api.get('/catalogos/programas-sociales');
      setProgramas(r.data.programas || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const iniciarEdicion = (p) => {
    setEditando(p.id);
    setFormEdit({ etiqueta: p.etiqueta, orden: p.orden });
  };

  const guardarEdicion = async (id) => {
    try {
      await api.put(`/catalogos/programas-sociales/${id}`, formEdit);
      setMensaje({ tipo: 'exito', texto: '✅ Programa actualizado.' });
      setEditando(null);
      fetchProgramas();
    } catch (e) {
      setMensaje({ tipo: 'error', texto: e.response?.data?.error || 'Error al actualizar.' });
    }
  };

  const toggleActivo = async (p) => {
    try {
      await api.put(`/catalogos/programas-sociales/${p.id}`, { activo: !p.activo });
      fetchProgramas();
    } catch (e) {
      setMensaje({ tipo: 'error', texto: 'Error al cambiar estatus.' });
    }
  };

  const eliminar = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.etiqueta}"?`)) return;
    try {
      const r = await api.delete(`/catalogos/programas-sociales/${p.id}`);
      setMensaje({ tipo: 'exito', texto: r.data.mensaje });
      fetchProgramas();
    } catch (e) {
      setMensaje({ tipo: 'error', texto: e.response?.data?.error || 'Error al eliminar.' });
    }
  };

  const crearNuevo = async () => {
    if (!formNuevo.clave.trim() || !formNuevo.etiqueta.trim()) {
      setMensaje({ tipo: 'error', texto: 'Clave y etiqueta son obligatorios.' });
      return;
    }
    try {
      await api.post('/catalogos/programas-sociales', formNuevo);
      setMensaje({ tipo: 'exito', texto: '✅ Programa agregado.' });
      setFormNuevo({ clave: '', etiqueta: '', orden: 0 });
      setMostrarNuevo(false);
      fetchProgramas();
    } catch (e) {
      setMensaje({ tipo: 'error', texto: e.response?.data?.error || 'Error al crear.' });
    }
  };

  const moverOrden = async (p, direccion) => {
    const nuevoOrden = direccion === 'up' ? p.orden - 1 : p.orden + 1;
    try {
      await api.put(`/catalogos/programas-sociales/${p.id}`, { orden: nuevoOrden });
      fetchProgramas();
    } catch (e) { console.error(e); }
  };

  if (!puedeAcceder) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-500 text-lg">No tienes permiso para ver esta página.</p>
          <button onClick={() => navigate('/dashboard')}
            className="mt-4 text-green-700 underline">Volver al Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">

      <header className="text-white px-6 py-4 flex items-center justify-between shadow-lg"
        style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)' }}>
        <img src={logo} alt="DOMINIUM" className="h-14" />
        <button onClick={() => navigate('/dashboard')}
          className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg text-sm transition">
          ← Volver al Dashboard
        </button>
      </header>

      <main className="p-4 max-w-3xl mx-auto">

        <div className="mb-5 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-800">🏛️ Catálogo de Programas Sociales</h2>
            <p className="text-gray-500 text-sm">Agrega, edita o desactiva los programas que aparecen en Toca y Promueve</p>
          </div>
          <button onClick={() => setMostrarNuevo(!mostrarNuevo)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium transition"
            style={{ background: 'linear-gradient(135deg, #16a34a, #15803d)' }}>
            <Plus size={16} /> Nuevo
          </button>
        </div>

        {mensaje && (
          <div className={`p-3 rounded-lg text-sm mb-4 flex items-center justify-between ${
            mensaje.tipo === 'exito' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            <span>{mensaje.texto}</span>
            <button onClick={() => setMensaje(null)}><X size={14} /></button>
          </div>
        )}

        {/* Formulario nuevo */}
        {mostrarNuevo && (
          <div className="bg-white rounded-xl shadow-sm p-4 mb-4 border-2 border-green-200">
            <p className="text-sm font-semibold text-gray-700 mb-3">➕ Nuevo programa social</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Nombre del programa</label>
                <input value={formNuevo.etiqueta}
                  onChange={e => setFormNuevo(prev => ({ ...prev, etiqueta: e.target.value, clave: e.target.value }))}
                  placeholder="Ej: Apoyo a Cafeticultores"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Orden (posición en la lista)</label>
                <input type="number" value={formNuevo.orden}
                  onChange={e => setFormNuevo(prev => ({ ...prev, orden: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div className="flex items-end gap-2">
                <button onClick={crearNuevo}
                  className="flex-1 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition">
                  Guardar
                </button>
                <button onClick={() => setMostrarNuevo(false)}
                  className="flex-1 py-2 rounded-lg border border-gray-300 text-gray-600 text-sm font-medium hover:bg-gray-50 transition">
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Lista de programas */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Cargando...</div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-100">
            {programas.map((p, idx) => (
              <div key={p.id} className={`p-4 flex items-center gap-3 ${!p.activo ? 'opacity-50' : ''}`}>

                {/* Orden */}
                <div className="flex flex-col gap-1">
                  <button onClick={() => moverOrden(p, 'up')} disabled={idx === 0}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-20">
                    <ArrowUp size={14} />
                  </button>
                  <button onClick={() => moverOrden(p, 'down')} disabled={idx === programas.length - 1}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-20">
                    <ArrowDown size={14} />
                  </button>
                </div>

                {/* Contenido */}
                <div className="flex-1">
                  {editando === p.id ? (
                    <input value={formEdit.etiqueta}
                      onChange={e => setFormEdit(prev => ({ ...prev, etiqueta: e.target.value }))}
                      className="w-full px-2 py-1 border border-green-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500" />
                  ) : (
                    <>
                      <p className="text-sm font-medium text-gray-800">{p.etiqueta}</p>
                      <p className="text-xs text-gray-400">clave: {p.clave} {!p.activo && '• (desactivado)'}</p>
                    </>
                  )}
                </div>

                {/* Acciones */}
                <div className="flex items-center gap-2">
                  {editando === p.id ? (
                    <>
                      <button onClick={() => guardarEdicion(p.id)}
                        className="p-2 rounded-lg bg-green-100 text-green-700 hover:bg-green-200 transition">
                        <Check size={16} />
                      </button>
                      <button onClick={() => setEditando(null)}
                        className="p-2 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 transition">
                        <X size={16} />
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => toggleActivo(p)}
                        className={`px-2 py-1 rounded-lg text-xs font-medium transition ${
                          p.activo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                        {p.activo ? 'Activo' : 'Inactivo'}
                      </button>
                      <button onClick={() => iniciarEdicion(p)}
                        className="p-2 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 transition">
                        <Edit2 size={14} />
                      </button>
                      <button onClick={() => eliminar(p)}
                        className="p-2 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition">
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-center text-gray-400 text-xs mt-8">
          DOMINIUM © 2026 — Zacatlán, Puebla | GARCOR Design
        </p>
      </main>
    </div>
  );
}
